import type { CSSProperties, ReactNode } from 'react'

/**
 * The frosted pane everything sits on. One shape, two states: ordinary, and
 * the attention card that a hairline of accent and a warm wash lift out of the
 * stack (the overdue tile, the current plan, the row you are being asked to
 * look at). Nothing else changes between them — a card doesn't get bigger,
 * bolder or shadowed differently to say it matters.
 */

interface CardProps {
  children: ReactNode
  /** The `var(--w-attention-line)` rim on `--w-pane-hi`. */
  attention?: boolean
  /** Outer cards are 14px; a panel nested inside one is 11–12px. */
  radius?: number
  className?: string
  style?: CSSProperties
  onClick?: () => void
}

export function Card({
  children,
  attention = false,
  radius = 14,
  className = '',
  style,
  onClick,
}: CardProps) {
  return (
    <div
      className={`fr ${onClick ? 'tab' : ''} ${className}`}
      onClick={onClick}
      style={{
        borderRadius: radius,
        border: attention ? '1px solid var(--w-attention-line)' : '1px solid var(--w-line)',
        background: attention ? 'var(--w-pane-hi)' : 'var(--w-pane)',
        boxShadow: 'var(--w-inset),var(--w-lift)',
        overflow: 'hidden',
        ...(onClick ? { cursor: 'pointer' } : null),
        ...style,
      }}
    >
      {children}
    </div>
  )
}

interface CardHeaderProps {
  children: ReactNode
  /**
   * Right-aligned controls — an action chip, a segmented control, a menu
   * trigger. Controls, not prose: a sentence here competes with the label for
   * a row that has room for one of them, and wraps to its own line on a phone.
   * Something to *say* about the card belongs in its body.
   */
  actions?: ReactNode
  /** The accent label the attention card uses in place of muted. */
  attention?: boolean
  style?: CSSProperties
}

/**
 * The label strip along the top of a card. Uppercase and tracked wide, so it
 * reads as a heading for the card rather than as the first line of it.
 */
export function CardHeader({ children, actions, attention = false, style }: CardHeaderProps) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        // Wraps rather than collides. The label shrinks to nothing before a
        // wide `actions` will give any room back, so on a phone a long one was
        // drawn straight over the top of it — "PAYMENT PHASES" clipped to 30px
        // with a sentence across it. Nothing changes at a width where both fit.
        flexWrap: 'wrap',
        rowGap: 4,
        padding: '13px 16px',
        borderBottom: '1px solid var(--w-line)',
        ...style,
      }}
    >
      <span
        style={{
          flex: 1,
          // A floor, not just minWidth:0 — below this the label is unreadable
          // and dropping the actions to their own line is the better answer.
          minWidth: 120,
          font: '600 11px Manrope,sans-serif',
          letterSpacing: '.14em',
          textTransform: 'uppercase',
          color: attention ? 'var(--color-action-ink)' : 'var(--color-muted)',
        }}
      >
        {children}
      </span>
      {actions}
    </div>
  )
}

interface RowProps {
  children: ReactNode
  /** The last row in a card drops its rule, so the card's own edge is the line. */
  last?: boolean
  onClick?: () => void
  style?: CSSProperties
  className?: string
}

/**
 * One line inside a card: something on the left, something to do with it on
 * the right. 13px 16px and a --w-line-soft rule, which is the quieter hairline
 * — --w-line separates sections, this separates items within one.
 */
export function Row({ children, last = false, onClick, style, className = '' }: RowProps) {
  return (
    <div
      className={`${onClick ? 'tab' : ''} ${className}`}
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        padding: '13px 16px',
        borderBottom: last ? undefined : '1px solid var(--w-line-soft)',
        flexWrap: 'wrap',
        ...(onClick ? { cursor: 'pointer' } : null),
        ...style,
      }}
    >
      {children}
    </div>
  )
}

/**
 * A label, then a rule that fades out from the accent. Used where a stack of
 * cards needs breaking into named groups without another box around them.
 */
/**
 * A card's prose half — an explanation, a hint, a stack of fields — as against
 * `Row`, which is a label with a control on the right.
 *
 * Promoted from six identical local copies (2026-09-01): WebsiteSection,
 * InvoicingSection and the four wizards had each written `padding: 16, column,
 * gap: 12` under the name `Body`, character for character. They had not drifted
 * yet, which is the argument for moving them before they do.
 */
export function CardBody({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return (
    <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12, ...style }}>
      {children}
    </div>
  )
}

/**
 * The muted line under a control, or the paragraph that stands in for one.
 *
 * The other half of the same promotion, and the more copied of the two: the
 * same three declarations existed in six files. `Field` carries its own `note`
 * for the common case; this is for the prose that is not attached to a field.
 */
export function Hint({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return (
    <div
      style={{
        font: '400 11px/1.6 Manrope,sans-serif',
        color: 'var(--color-muted)',
        textWrap: 'pretty',
        ...style,
      }}
    >
      {children}
    </div>
  )
}

export function SectionDivider({ children }: { children: ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <span
        style={{
          font: '600 11px Manrope,sans-serif',
          letterSpacing: '.14em',
          textTransform: 'uppercase',
          color: 'var(--color-muted)',
          flex: 'none',
        }}
      >
        {children}
      </span>
      <span
        style={{
          flex: 1,
          height: 1,
          background: 'linear-gradient(90deg, var(--w-attention-line), var(--w-line-soft))',
        }}
      />
    </div>
  )
}
